import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';
import { Plus, Trash2 } from 'lucide-react';

interface CollectionRule {
  id: string;
  name: string;
  idade_min: number | null;
  idade_max: number | null;
  menos_que: boolean | null;
  dias_atraso: number | null;
  estado: string | null;
  cidade: string | null;
  bairro: string | null;
  prioridade: number | null;
  custom_criteria?: { field: string; operator: string; value: string }[] | null;
}

interface ReguaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: CollectionRule | null;
  onSaved: () => void;
}

interface CustomCriteria {
  field: string;
  operator: string;
  value: string;
}

const operators = [
  { id: 'equals', label: 'Igual a' },
  { id: 'not_equals', label: 'Diferente de' },
  { id: 'contains', label: 'Contém' },
  { id: 'greater_than', label: 'Maior que' },
  { id: 'less_than', label: 'Menor que' },
];

export function ReguaModal({ open, onOpenChange, rule, onSaved }: ReguaModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    idade_min: '',
    idade_max: '',
    menos_que: true,
    dias_atraso: '',
    estado: '',
    cidade: '',
    bairro: ''
  });
  const [customCriteria, setCustomCriteria] = useState<CustomCriteria[]>([]);
  const [showCustomCriteria, setShowCustomCriteria] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      if (rule) {
        setFormData({
          name: rule.name,
          idade_min: rule.idade_min?.toString() || '',
          idade_max: rule.idade_max?.toString() || '',
          menos_que: rule.menos_que ?? true,
          dias_atraso: rule.dias_atraso?.toString() || '',
          estado: rule.estado || '',
          cidade: rule.cidade || '',
          bairro: rule.bairro || ''
        });
        if (rule.custom_criteria && Array.isArray(rule.custom_criteria)) {
          setCustomCriteria(rule.custom_criteria);
          setShowCustomCriteria(rule.custom_criteria.length > 0);
        } else {
          setCustomCriteria([]);
          setShowCustomCriteria(false);
        }
      } else {
        setFormData({
          name: '',
          idade_min: '',
          idade_max: '',
          menos_que: true,
          dias_atraso: '',
          estado: '',
          cidade: '',
          bairro: ''
        });
        setCustomCriteria([]);
        setShowCustomCriteria(false);
      }
      setError('');
    }
  }, [open, rule]);

  const addCustomCriteria = () => {
    setCustomCriteria(prev => [...prev, { field: '', operator: 'equals', value: '' }]);
  };

  const removeCustomCriteria = (index: number) => {
    setCustomCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const updateCustomCriteria = (index: number, updates: Partial<CustomCriteria>) => {
    setCustomCriteria(prev => prev.map((c, i) => i === index ? { ...c, ...updates } : c));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!formData.name.trim()) {
        throw new Error('Nome é obrigatório');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      // Get max priority for new rules
      let prioridade = rule?.prioridade ?? 0;
      if (!rule) {
        const { data: maxPrioridade } = await supabase
          .from('collection_rules')
          .select('prioridade')
          .eq('company_id', profile.company_id)
          .order('prioridade', { ascending: false })
          .limit(1)
          .single();
        
        prioridade = (maxPrioridade?.prioridade ?? 0) + 1;
      }

      // Filter out empty custom criteria and convert to JSON-compatible format
      const validCustomCriteria = customCriteria
        .filter(c => c.field && c.value)
        .map(c => ({ field: c.field, operator: c.operator, value: c.value }));

      const payload = {
        name: formData.name.trim(),
        idade_min: formData.idade_min ? parseInt(formData.idade_min) : null,
        idade_max: formData.idade_max ? parseInt(formData.idade_max) : null,
        menos_que: formData.menos_que,
        dias_atraso: formData.dias_atraso ? parseInt(formData.dias_atraso) : null,
        estado: formData.estado || null,
        cidade: formData.cidade || null,
        bairro: formData.bairro || null,
        prioridade,
        company_id: profile.company_id,
        custom_criteria: validCustomCriteria.length > 0 ? JSON.parse(JSON.stringify(validCustomCriteria)) : null,
        // Required fields with defaults
        trigger_days: 0,
        channel: 'whatsapp' as const
      };

      if (rule) {
        const { error } = await supabase
          .from('collection_rules')
          .update(payload)
          .eq('id', rule.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('collection_rules')
          .insert(payload);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
      onSaved();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {rule ? 'Editar Régua' : 'Nova Régua'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Nome da Régua */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome da Régua *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Clientes jovens inadimplentes"
            />
          </div>

          {/* Critério de Idade */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Critério de Idade
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="idade_min">Idade mínima</Label>
                <Input
                  id="idade_min"
                  type="number"
                  min={0}
                  max={120}
                  value={formData.idade_min}
                  onChange={(e) => setFormData(prev => ({ ...prev, idade_min: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="idade_max">Idade máxima</Label>
                <Input
                  id="idade_max"
                  type="number"
                  min={0}
                  max={120}
                  value={formData.idade_max}
                  onChange={(e) => setFormData(prev => ({ ...prev, idade_max: e.target.value }))}
                  placeholder="120"
                />
              </div>
            </div>
          </div>

          {/* Critério de Pontualidade */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Critério de Pontualidade
            </p>
            <div className="flex gap-2 items-center">
              <span className="text-sm">Atrasou</span>
              <select
                value={formData.menos_que ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, menos_que: e.target.value === 'true' }))}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="true">menos que</option>
                <option value="false">a partir de</option>
              </select>
              <Input
                type="number"
                min={0}
                value={formData.dias_atraso}
                onChange={(e) => setFormData(prev => ({ ...prev, dias_atraso: e.target.value }))}
                className="w-24"
                placeholder="0"
              />
              <span className="text-sm">dias</span>
            </div>
          </div>

          {/* Critério de Endereço */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Critério de Endereço
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label htmlFor="estado">Estado</Label>
                <select
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    estado: e.target.value || '',
                    cidade: e.target.value ? prev.cidade : '',
                    bairro: e.target.value ? prev.bairro : ''
                  }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Todos</option>
                  <option disabled>---</option>
                  {ESTADOS_BRASILEIROS.map(estado => (
                    <option key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={formData.cidade}
                  onChange={(e) => setFormData(prev => ({ ...prev, cidade: e.target.value }))}
                  placeholder="Qualquer"
                  disabled={!formData.estado}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  value={formData.bairro}
                  onChange={(e) => setFormData(prev => ({ ...prev, bairro: e.target.value }))}
                  placeholder="Qualquer"
                  disabled={!formData.estado}
                />
              </div>
            </div>
          </div>

          {/* Critério Customizado */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Critério Customizado
              </p>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowCustomCriteria(!showCustomCriteria);
                  if (!showCustomCriteria && customCriteria.length === 0) {
                    addCustomCriteria();
                  }
                }}
                className="text-xs"
              >
                {showCustomCriteria ? 'Ocultar' : 'Adicionar'} (Opcional)
              </Button>
            </div>
            
            {showCustomCriteria && (
              <div className="space-y-3 p-3 rounded-lg bg-muted/30 border">
                <p className="text-xs text-muted-foreground">
                  Use campos customizados da sua base de clientes para filtrar.
                </p>
                
                {customCriteria.map((criteria, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      value={criteria.field}
                      onChange={(e) => updateCustomCriteria(index, { field: e.target.value })}
                      placeholder="Nome do campo"
                      className="flex-1"
                    />
                    <select
                      value={criteria.operator}
                      onChange={(e) => updateCustomCriteria(index, { operator: e.target.value })}
                      className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      {operators.map(op => (
                        <option key={op.id} value={op.id}>{op.label}</option>
                      ))}
                    </select>
                    <Input
                      value={criteria.value}
                      onChange={(e) => updateCustomCriteria(index, { value: e.target.value })}
                      placeholder="Valor"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive/70 hover:text-destructive"
                      onClick={() => removeCustomCriteria(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={addCustomCriteria}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar critério
                </Button>
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
